import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useItems from "../../api/items";
import {
  smartCompressImage,
  convertHeicIfNeeded,
  dataUrlToFile,
} from "../../lib/imageCompression";
import * as XLSX from "xlsx";

// UI Components
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

import {
  Pencil,
  Trash2,
  Plus,
  Search,
  PackagePlus,
  Download,
  Settings,
  ChevronDown,
  Layers,
  Tag,
  AlertCircle,
  Check,
  GripVertical,
  Eye,
  EyeOff,
  X,
  Languages,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

// Import the ItemEditor component
import ItemEditor from "./ItemEditor";

// Helper to strip domain from S3 URLs
const stripDomain = (val) => {
  if (typeof val !== "string") return val;
  if (val.startsWith("http")) {
    try {
      const url = new URL(val);
      // If pathname contains /images/ or /videos/, extract from there
      if (url.pathname.includes("/images/")) {
        const parts = url.pathname.split("/images/");
        return "images/" + parts[1];
      }
      if (url.pathname.includes("/videos/")) {
        const parts = url.pathname.split("/videos/");
        return "videos/" + parts[1];
      }
      // Fallback
      return url.pathname.startsWith("/")
        ? url.pathname.substring(1)
        : url.pathname;
    } catch (e) {
      console.error("Error stripping domain:", e);
      return val;
    }
  }
  return val;
};

// Helper to clean item data (merge legacy fields, strip URLs)
const cleanItemData = (item) => {
  const newItem = { ...item };

  // 1. Merge/Delete 'afbeelding' into 'afbeelding_image'
  if ("afbeelding" in newItem) {
    const val = newItem["afbeelding"];
    if (val) {
      const valArray = Array.isArray(val) ? val : [val];
      const cleanVals = valArray.map(stripDomain);

      // Check if these values exist in ANY other image field (excluding afbeelding_image)
      // If so, it's likely an auto-generated fallback from the backend, so we shouldn't merge it into afbeelding_image
      const otherImages = new Set();
      Object.keys(newItem).forEach((key) => {
        if (
          key !== "afbeelding" &&
          key !== "afbeelding_image" &&
          (key.includes("image") || key.includes("afbeelding"))
        ) {
          const v = newItem[key];
          const va = Array.isArray(v) ? v : [v];
          va.forEach((x) => {
            if (x) otherImages.add(stripDomain(x));
          });
        }
      });

      // Filter out values that exist in other fields
      const uniqueVals = cleanVals.filter((v) => !otherImages.has(v));

      if (uniqueVals.length > 0) {
        // Ensure 'afbeelding_image' exists
        if (!newItem["afbeelding_image"]) {
          newItem["afbeelding_image"] = [];
        }

        // Normalize existing to array
        let existing = newItem["afbeelding_image"];
        if (!Array.isArray(existing)) {
          existing = existing ? [existing] : [];
        }

        // We need to strip domain BEFORE checking for duplicates
        const existingClean = existing.map(stripDomain);

        uniqueVals.forEach((v) => {
          if (v && !existingClean.includes(v)) {
            existingClean.push(v);
          }
        });

        newItem["afbeelding_image"] = existingClean;
      }
    }
    delete newItem["afbeelding"];
  }

  // 2. Strip domains from all image and video fields
  Object.keys(newItem).forEach((key) => {
    if (
      (key.includes("image") ||
        key.includes("afbeelding") ||
        key.includes("video")) &&
      newItem[key]
    ) {
      const val = newItem[key];
      if (Array.isArray(val)) {
        newItem[key] = val.map((v) => stripDomain(v));
      } else if (typeof val === "string") {
        newItem[key] = stripDomain(val);
      }
    }
  });

  return newItem;
};

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

// Helper function to get display value from potentially translated field
const getDisplayValue = (value, preferredLang = "nl") => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    // It's a translation object
    return value[preferredLang] || value.nl || value.fr || value.en || "";
  }
  return value;
};

// Resolve the current display label for a category-linked select field
// (a field named "<categorie>_select"). The stored value is the referenced
// item's id. We ALWAYS look the name up live from the source category so a
// rename of the source item propagates immediately.
// Backwards compatible with legacy stored formats:
//   - "12:Stadsreiniger"  (id + snapshot label)
//   - "12"                (id only, current format)
//   - "Stadsreiniger"     (raw label, oldest data without ids)
const resolveSelectDisplay = (field, value, allItems, preferredLang = "nl") => {
  if (value === null || value === undefined || value === "") return "";

  // Only category-linked selects ("<cat>_select") reference another list.
  // Manual selects ("<name>_select[a|b|c]") store their own literal value.
  const isCategoryLinked = /_select$/.test(field) && !field.includes("[");
  if (!isCategoryLinked) return String(getDisplayValue(value, preferredLang));

  const categoryName = field.replace(/_select$/, "");
  const sourceList = (allItems && allItems[categoryName]) || [];

  // Extract the referenced id (strip a legacy snapshot label if present).
  const rawId = String(value).includes(":")
    ? String(value).split(":")[0]
    : String(value);

  const source = sourceList.find((i) => String(i.id) === String(rawId));
  if (source) {
    return String(getDisplayValue(source.name, preferredLang));
  }

  // Source item not found (deleted, or value is a legacy raw label).
  if (String(value).includes(":")) return String(value).split(":")[1];
  return String(value);
};

const ItemsManager = ({ targetCompanyId = null }) => {
  const { company, isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const {
    getCurrentCompanyItems,
    updateCurrentCompanyItems,
    deleteCurrentCompanyItems,
    getItemsByCompanyId,
    updateItemsByCompanyId,
    deleteItemsByCompanyId,
    uploadImage,
    uploadMedia,
    getImageUrl,
    deleteImage,
  } = useItems();

  // Core state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState({});
  const [editedItems, setEditedItems] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categoryFields, setCategoryFields] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, percent, fileName }
  const [uploadError, setUploadError] = useState(null);

  // Category-wide auto-translate state
  const [translateCategoryDialogOpen, setTranslateCategoryDialogOpen] =
    useState(false);
  const [isTranslatingCategory, setIsTranslatingCategory] = useState(false);
  const [translateProgress, setTranslateProgress] = useState({
    current: 0,
    total: 0,
  });
  const [translateSourceLang, setTranslateSourceLang] = useState("nl");
  const [translateTargetLangs, setTranslateTargetLangs] = useState(["fr", "en"]);
  const [translateOverwrite, setTranslateOverwrite] = useState(false);

  useEffect(() => {
    console.log(
      "ItemsManager mounted/updated. targetCompanyId:",
      targetCompanyId,
    );
    fetchItems();
  }, [targetCompanyId]);

  // Update category fields when items change (preserve visual order using first item's key order)
  useEffect(() => {
    const newCategoryFields = {};
    let itemCount = 0;

    Object.keys(items).forEach((category) => {
      itemCount += items[category].length;

      const categoryItems = items[category];
      if (!categoryItems || categoryItems.length === 0) return;

      // Start with the first item's keys to preserve order
      const first = categoryItems[0];
      const ordered = Object.keys(first).filter(
        (k) => !["id", "_fieldMetadata", "_translations"].includes(k),
      );
      const seen = new Set(ordered);

      // Append any missing keys from other items in the order encountered
      categoryItems.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (
            !seen.has(key) &&
            !["id", "_fieldMetadata", "_translations"].includes(key)
          ) {
            ordered.push(key);
            seen.add(key);
          }
        });
      });

      newCategoryFields[category] = ordered;
    });

    setCategoryFields(newCategoryFields);
    setTotalItems(itemCount);
  }, [items]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      console.log("Fetching items. targetCompanyId:", targetCompanyId);
      const data =
        targetCompanyId !== null && targetCompanyId !== undefined
          ? await getItemsByCompanyId(targetCompanyId)
          : await getCurrentCompanyItems();
      console.log(
        "Items fetched:",
        data ? Object.keys(data).length + " categories" : "No data",
      );

      // Clean all items immediately after fetching to ensure no full URLs are in state
      const cleanedData = {};
      if (data && typeof data === "object") {
        Object.keys(data).forEach((category) => {
          if (Array.isArray(data[category])) {
            cleanedData[category] = data[category].map((item) =>
              cleanItemData(item),
            );
          } else {
            cleanedData[category] = data[category];
          }
        });
      }

      setItems(cleanedData);
      // Set the first category as active tab if exists
      const categories = Object.keys(cleanedData);
      if (categories.length > 0 && !activeTab) {
        setActiveTab(categories[0]);
      }
    } catch (err) {
      console.error("Error fetching items:", err);
      setError("Er is een fout opgetreden bij het ophalen van uw items.");
    } finally {
      setLoading(false);
    }
  };

  const findImageField = (item) => {
    // Look for fields that contain image-like keywords or have image data URL values
    const imageField = Object.keys(item).find((key) => {
      return (
        // Check if field name suggests it's an image
        /image|afbeelding|foto|picture/i.test(key) &&
        // And has a value (either string or number)
        item[key] !== "" &&
        item[key] !== null &&
        item[key] !== undefined
      );
    });

    console.log("findImageField called");
    console.log(imageField);
    if (imageField) {
      const val = item[imageField];
      if (Array.isArray(val)) {
        const first = val[0];
        if (typeof first === "number") {
          // Legacy numeric ID - needs to be fetched
          return { field: imageField, value: first, isId: true };
        } else if (typeof first === "string" && first.startsWith("images/")) {
          // S3 key - needs to be fetched
          return { field: imageField, value: first, isId: true };
        } else if (
          typeof first === "string" &&
          first.startsWith("data:image/")
        ) {
          // Base64 data URL - can be displayed directly
          return { field: imageField, value: first, isId: false };
        }
      } else if (typeof val === "number") {
        // Legacy numeric ID - needs to be fetched
        return { field: imageField, value: val, isId: true };
      } else if (typeof val === "string" && val.startsWith("images/")) {
        // S3 key - needs to be fetched
        return { field: imageField, value: val, isId: true };
      } else if (typeof val === "string" && val.startsWith("data:image/")) {
        // Base64 data URL - can be displayed directly
        return { field: imageField, value: val, isId: false };
      }
    }

    return null;
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    // If not admin, redirect to tickets
    if (!isAdmin) {
      setAddDialogOpen(false);
      navigate(
        `/dashboard/tickets?newTicket=true&title=Aanvraag nieuwe categorie: ${encodeURIComponent(
          newCategoryName.trim(),
        )}`,
      );
      return;
    }

    const newItems = {
      ...items,
      [newCategoryName.trim()]: [],
    };
    setItems(newItems);
    setActiveTab(newCategoryName.trim());
    setAddDialogOpen(false);
    setNewCategoryName("");

    try {
      setSaving(true);
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, newItems);
      } else {
        await updateCurrentCompanyItems(newItems);
      }
      setSuccess("Categorie toegevoegd");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Error saving category:", err);
      setError("Er is een fout opgetreden bij het opslaan van de categorie.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = (category) => {
    const newItem = { id: Date.now(), name: "Nieuw item" };

    if (items[category].length > 0) {
      categoryFields[category]?.forEach((field) => {
        if (
          field !== "id" &&
          field !== "name" &&
          field !== "_fieldMetadata" &&
          field !== "_translations" &&
          !newItem[field]
        ) {
          newItem[field] = "";
        }
      });
    }

    // Inherit the multi-language setting from the category: if existing items
    // have translations enabled, a new item starts with them enabled too
    // (same languages). The user still fills in the values themselves.
    const translatedSibling = (items[category] || []).find(
      (it) => it._translations?.enabled,
    );
    if (translatedSibling) {
      const languages = translatedSibling._translations.languages || [
        "nl",
        "fr",
        "en",
      ];
      const primary = languages[0] || "nl";

      Object.keys(newItem).forEach((field) => {
        if (field === "id" || field === "_translations") return;
        if (!shouldFieldBeTranslated(field, newItem[field])) return;
        // Leave array fields as-is; their elements become translatable when added.
        if (detectFieldType(field, newItem[field]) === "array") return;

        const current = newItem[field];
        const obj = languages.reduce((acc, l) => ({ ...acc, [l]: "" }), {});
        // Preserve any existing scalar (e.g. the default name) under the primary language.
        if (current != null && current !== "" && typeof current !== "object") {
          obj[primary] = current;
        }
        newItem[field] = obj;
      });

      newItem._translations = { enabled: true, languages };
    }

    setEditedItems({ category, item: newItem });
    setEditDialogOpen(true);
  };

  const handleStartEditing = (category, item) => {
    if (
      editedItems &&
      (editedItems.category !== category || editedItems.item.id !== item.id)
    ) {
      setConfirmDialogTitle("Niet-opgeslagen wijzigingen");
      setConfirmDialogMessage(
        "U heeft niet-opgeslagen wijzigingen. Weet u zeker dat u wilt switchen?",
      );
      setConfirmDialogAction(() => () => {
        setEditedItems({ category, item: { ...item } });
        setConfirmDialogOpen(false);
        setEditDialogOpen(true);
      });
      setConfirmDialogOpen(true);
      return;
    }

    setEditedItems({ category, item: { ...item } });
    setEditDialogOpen(true);
  };

  const handleCancelEditing = () => {
    setEditedItems(null);
    setEditDialogOpen(false);
  };

  const detectFieldType = (field, value) => {
    // First check if it's a select field with options
    if (field.includes("[") && field.includes("]")) {
      return "select";
    }

    // Explicit name-based checks
    if (/_unit$/.test(field)) {
      return "unit";
    }
    if (field.includes("_array")) {
      return "array";
    }
    if (field.includes("_date") || field.toLowerCase().includes("datum")) {
      return "date";
    }

    // Then check value type
    if (typeof value === "boolean") {
      return "checkbox";
    }
    if (typeof value === "number") {
      return "number";
    }
    // Check for translation objects (skip them, they're text fields)
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (value.nl || value.fr || value.en)
    ) {
      return "text";
    }
    // Check for array of strings (or translation objects)
    if (
      Array.isArray(value) &&
      value.every(
        (item) =>
          typeof item === "string" ||
          (typeof item === "object" && (item.nl || item.fr || item.en)),
      )
    ) {
      return "array";
    }
    if (value && typeof value === "object" && value.type === "image") {
      return "image";
    }

    // Try to detect ISO date strings (yyyy-MM-dd)
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return "date";
    }

    // Check field name patterns for number fields
    if (isNumberField(field)) {
      return "number";
    }

    // Finally check other field name patterns
    if (field.includes("_check")) {
      return "checkbox";
    }
    if (
      field.includes("_image") ||
      field.toLowerCase().includes("afbeelding")
    ) {
      return "image";
    }
    if (field.includes("_description")) {
      return "textarea";
    }
    if (field.includes("_select")) {
      return "select";
    }

    return "text";
  };

  // Helper function to extract image IDs from an item
  // Helper function to extract image keys from an item (now supports both S3 keys and legacy numeric IDs)
  const extractImageIds = (item) => {
    const imageKeys = [];

    Object.keys(item).forEach((field) => {
      const fieldType = detectFieldType(field, item[field]);
      if (fieldType !== "image") return;

      const val = item[field];
      if (Array.isArray(val)) {
        val.forEach((v) => {
          // Support both S3 keys (strings) and legacy numeric IDs
          if (
            typeof v === "string" &&
            (v.startsWith("images/") || v.includes("/images/"))
          ) {
            imageKeys.push(v); // S3 key or URL
          } else if (typeof v === "number") {
            imageKeys.push(v); // Legacy numeric ID
          }
        });
      } else if (
        typeof val === "string" &&
        (val.startsWith("images/") || val.includes("/images/"))
      ) {
        imageKeys.push(val); // S3 key or URL
      } else if (typeof val === "number") {
        imageKeys.push(val); // Legacy numeric ID
      }
      // If it's a base64 string, we can't extract a key/ID (inline data), skip
    });

    return imageKeys;
  };

  // Helper function to delete image from S3 or database (supports both)
  const cleanupImage = async (imageKey) => {
    if (!imageKey) return;

    // Skip if it's a base64 data URL (inline data)
    if (typeof imageKey === "string" && imageKey.startsWith("data:")) return;

    // Extract key from URL if needed (e.g. if full URL was stored)
    let keyToDelete = imageKey;
    if (typeof imageKey === "string" && imageKey.includes("/images/")) {
      try {
        const parts = imageKey.split("/images/");
        // Keep "images/" prefix and remove query params
        keyToDelete = "images/" + parts[1].split("?")[0];
      } catch (e) {
        console.warn("Failed to extract key from URL:", imageKey);
      }
    }

    try {
      console.log("Deleting image:", keyToDelete);
      await deleteImage(keyToDelete); // Works for both S3 keys and numeric IDs
      console.log("Image deleted successfully:", keyToDelete);
    } catch (error) {
      console.error("Error deleting image:", keyToDelete, error);
      // Don't throw - we don't want to break the main operation if image cleanup fails
    }
  };

  // Get clean display name for a field (removes suffixes)
  const getFieldDisplayName = (fieldName) => {
    // Remove type suffixes and options for display
    let displayName = fieldName;

    // Remove select options [option1|option2]
    displayName = displayName.replace(/\[.*?\]$/, "");

    // Remove type suffixes
    // For unit fields, pattern is <name>_<unit>_unit
    displayName = displayName.replace(/_(.*?)_unit$/, "");
    displayName = displayName.replace(/_check$/, "");
    displayName = displayName.replace(/_number$/, "");
    displayName = displayName.replace(/_image$/, "");
    displayName = displayName.replace(/_description$/, "");
    displayName = displayName.replace(/_select$/, "");
    displayName = displayName.replace(/_date$/, "");
    displayName = displayName.replace(/_array$/, "");

    return displayName;
  };

  // Get unit suffix from a field name like <name>_<unit>_unit
  const getFieldUnit = (fieldName) => {
    if (!/_unit$/.test(fieldName)) return undefined;
    const m = fieldName.match(/_(.*?)_unit$/);
    return m && m[1] ? m[1] : undefined;
  };

  const handleFinishEditing = async () => {
    if (!editedItems) return;

    setSaving(true);
    const { category, item } = editedItems;

    // Clean the item being edited (remove legacy fields, strip URLs)
    const cleanedItem = cleanItemData(item);

    try {
      // Determine if we're adding a brand-new item (id not present yet)
      const categoryItems = items[category] || [];
      const isNewItem = !categoryItems.some((it) => it.id === cleanedItem.id);

      // Ensure all existing items in the category have the same structure
      let updatedCategoryItems = categoryItems.map((existingItem) => {
        if (existingItem.id === cleanedItem.id) {
          return cleanedItem; // Replace when editing an existing item
        }
        // For other items, add any newly created fields from the editor
        const normalized = { ...existingItem };
        Object.keys(cleanedItem).forEach((key) => {
          if (!Object.prototype.hasOwnProperty.call(normalized, key)) {
            normalized[key] = null; // default for newly added fields
          }
        });
        // Also clean existing items to fix legacy data
        return cleanItemData(normalized);
      });

      // If it's a new item, append it now
      if (isNewItem) {
        updatedCategoryItems = [...updatedCategoryItems, cleanedItem];
      }

      const finalItems = {
        ...items,
        [category]: updatedCategoryItems,
      };

      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, finalItems);
      } else {
        await updateCurrentCompanyItems(finalItems);
      }
      setItems(finalItems);
      setSuccess("Item succesvol opgeslagen!");
    } catch (err) {
      setError(`Fout bij het opslaan van het item: ${err.message}`);
    } finally {
      setSaving(false);
      setEditDialogOpen(false);
      setEditedItems(null);
    }
  };

  const handleAddField = (fieldName, defaultValue) => {
    console.log("handleAddField in ItemsManager:", {
      fieldName,
      defaultValue,
      editedItems: editedItems?.item,
    });

    if (!editedItems || !fieldName) return;

    // Update the current item (no metadata stored)
    const updatedItem = {
      ...editedItems.item,
      [fieldName]: defaultValue,
    };

    console.log("Updated item after adding field:", updatedItem);

    // Auto-add the field to all other items in the same category with null value
    const categoryName = editedItems.category;
    const currentItemId = editedItems.item.id;

    console.log("Auto-adding field to other items in category:", categoryName);

    // Update all items in the category
    const updatedItems = { ...items };
    if (updatedItems[categoryName]) {
      updatedItems[categoryName] = updatedItems[categoryName].map((item) => {
        if (item.id === currentItemId) {
          // This is the current item, use the actual default value
          return updatedItem;
        } else if (!item.hasOwnProperty(fieldName)) {
          // This is another item that doesn't have this field yet, add it with null
          console.log(
            `Adding field "${fieldName}" with null to item "${getDisplayValue(
              item.name,
            )}"`,
          );
          return {
            ...item,
            [fieldName]: null,
          };
        } else {
          // Item already has this field, keep as is
          return item;
        }
      });
    }

    // Update the items state
    setItems(updatedItems);

    // Update the edited item state
    setEditedItems({ ...editedItems, item: updatedItem });
  };

  // Change a field's type (rename key and convert values) across ALL items in the category
  const handleChangeFieldType = (oldField, newType, payload = {}) => {
    if (!editedItems || !oldField || !newType) return;

    const category = editedItems.category;

    // Build new field name from display name and chosen type
    const baseName = getFieldDisplayName(oldField);

    const sanitizeUnitForKey = (unit) =>
      (unit || "").trim().replace(/\s+/g, "").replace(/_/g, "-");

    const buildFieldName = () => {
      switch (newType) {
        case "unit": {
          const unit = sanitizeUnitForKey(payload.unit || "");
          return unit ? `${baseName}_${unit}_unit` : `${baseName}_unit`;
        }
        case "number":
          return `${baseName}_number`;
        case "checkbox":
          return `${baseName}_check`;
        case "image":
          return `${baseName}_image`;
        case "textarea":
          return `${baseName}_description`;
        case "array":
          return `${baseName}_array`;
        case "select": {
          const opts = (payload.options || [])
            .map((o) => String(o).trim())
            .filter((o) => o.length > 0);
          return opts.length
            ? `${baseName}_select[${opts.join("|")}]`
            : `${baseName}_select`;
        }
        case "date":
          return `${baseName}_date`;
        case "text":
        default:
          return baseName;
      }
    };

    const newField = buildFieldName();
    if (newField === oldField) return;

    // Value converter for the new type
    const convertValue = (val) => {
      if (val === null || val === undefined) return val;

      // Handle translation objects - extract display value
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        (val.nl || val.fr || val.en)
      ) {
        val = getDisplayValue(val);
      }

      switch (newType) {
        case "number": {
          const n = Number(val);
          return isNaN(n) ? 0 : n;
        }
        case "checkbox": {
          if (typeof val === "boolean") return val;
          if (typeof val === "number") return val !== 0;
          if (typeof val === "string") {
            const s = val.toLowerCase();
            return s === "true" || s === "1" || s === "ja";
          }
          return false;
        }
        case "date":
          return String(val);
        case "textarea":
        case "text":
          return String(val);
        case "unit":
          return String(val);
        case "select":
          return String(val);
        case "array":
          // Convert to array
          if (Array.isArray(val)) return val;
          if (val === "") return [];
          // Split by comma if it's a comma-separated string
          if (typeof val === "string") {
            return val
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v !== "");
          }
          return [String(val)];
        case "image":
          return val; // keep existing id/base64
        default:
          return val;
      }
    };

    // For each item in the category, rename key and convert the value
    const newItems = { ...items };
    newItems[category] = (newItems[category] || []).map((it) => {
      if (!Object.prototype.hasOwnProperty.call(it, oldField)) return it;

      const converted = convertValue(it[oldField]);

      // rebuild object while preserving key order: replace in-place where oldField was
      const out = {};
      Object.keys(it).forEach((k) => {
        if (k === oldField) {
          out[newField] = converted;
        } else {
          out[k] = it[k];
        }
      });
      return out;
    });

    setItems(newItems);

    // Update editor state for current item as well
    const editedOut = (newItems[category] || []).find(
      (x) => x.id === editedItems.item.id,
    );
    if (editedOut) {
      setEditedItems({ category, item: editedOut });
    }

    // Save
    (async () => {
      try {
        setSaving(true);
        if (targetCompanyId !== null) {
          await updateItemsByCompanyId(targetCompanyId, newItems);
        } else {
          await updateCurrentCompanyItems(newItems);
        }
        setSuccess("Veldtype gewijzigd voor alle items in categorie");
        setTimeout(() => setSuccess(""), 1400);
      } catch (err) {
        console.error("Error changing field type:", err);
        setError("Kon veldtype niet opslaan voor alle items.");
      } finally {
        setSaving(false);
      }
    })();
  };

  // Convert a manual select field ("<name>_select[a|b|c]") into a category-linked
  // select ("<category>_select"). For every item in the current category we map
  // the stored literal label to the referenced source item's id, so a future
  // rename of that source item propagates live. Values without a matching source
  // item are kept untouched so nothing is lost.
  const handleConvertSelectToCategory = (oldField, sourceCategory) => {
    if (!editedItems || !oldField || !sourceCategory) return;

    const category = editedItems.category;
    const newField = `${sourceCategory}_select`;
    if (newField === oldField) return;

    const sourceList = items[sourceCategory] || [];

    // Build a case-insensitive label -> id lookup from the source category.
    const labelToId = new Map();
    sourceList.forEach((src) => {
      const name = String(getDisplayValue(src.name) || "")
        .trim()
        .toLowerCase();
      if (name && src.id != null && !labelToId.has(name)) {
        labelToId.set(name, String(src.id));
      }
    });

    const convertValue = (val) => {
      if (val === null || val === undefined || val === "") return "";
      // Reduce a translation object / legacy "id:label" snapshot to a plain label.
      let label = String(getDisplayValue(val) ?? "");
      if (label.includes(":")) {
        label = label.split(":").slice(1).join(":") || label;
      }
      const id = labelToId.get(label.trim().toLowerCase());
      return id != null ? id : String(val); // keep original if no match
    };

    // Rename the key (preserving order) and convert the value for every item.
    const newItems = { ...items };
    newItems[category] = (newItems[category] || []).map((it) => {
      if (!Object.prototype.hasOwnProperty.call(it, oldField)) return it;
      const converted = convertValue(it[oldField]);
      const out = {};
      Object.keys(it).forEach((k) => {
        if (k === oldField) out[newField] = converted;
        else out[k] = it[k];
      });
      return out;
    });

    setItems(newItems);

    const editedOut = (newItems[category] || []).find(
      (x) => x.id === editedItems.item.id,
    );
    if (editedOut) setEditedItems({ category, item: editedOut });

    (async () => {
      try {
        setSaving(true);
        if (targetCompanyId !== null) {
          await updateItemsByCompanyId(targetCompanyId, newItems);
        } else {
          await updateCurrentCompanyItems(newItems);
        }
        setSuccess(`Veld gekoppeld aan categorie "${sourceCategory}"`);
        setTimeout(() => setSuccess(""), 1600);
      } catch (err) {
        console.error("Error linking select to category:", err);
        setError("Kon de koppeling niet opslaan.");
      } finally {
        setSaving(false);
      }
    })();
  };

  // Reorder fields for ALL items in the category based on new order of 'extra' keys
  const handleReorderEditedItemFields = (newOrderKeys) => {
    if (!editedItems) return;

    const { category } = editedItems;

    // Update the state for the edited item
    setEditedItems((prev) => {
      if (!prev) return null;

      const reorderedItem = {};
      const currentItem = { ...prev.item };

      // Preserve 'id' and 'name' at the start
      if ("id" in currentItem) reorderedItem.id = currentItem.id;
      if ("name" in currentItem) reorderedItem.name = currentItem.name;

      const itemKeys = Object.keys(currentItem);
      const otherKeys = itemKeys.filter(
        (k) => !newOrderKeys.includes(k) && k !== "id" && k !== "name",
      );

      newOrderKeys.forEach((key) => {
        if (key in currentItem) {
          reorderedItem[key] = currentItem[key];
        }
      });

      otherKeys.forEach((key) => {
        reorderedItem[key] = currentItem[key];
      });

      return {
        ...prev,
        item: reorderedItem,
      };
    });

    // Also update the main items state to reflect the reordering for all items in the category
    setItems((prevItems) => {
      const updatedCategoryItems = (prevItems[category] || []).map((item) => {
        const reorderedItem = {};
        // Preserve 'id' and 'name' at the start
        if ("id" in item) reorderedItem.id = item.id;
        if ("name" in item) reorderedItem.name = item.name;

        // Get all other keys that are not part of the new order
        const itemKeys = Object.keys(item);
        const otherItemKeys = itemKeys.filter(
          (k) => !newOrderKeys.includes(k) && k !== "id" && k !== "name",
        );

        // Add the reordered extra fields
        newOrderKeys.forEach((key) => {
          if (key in item) {
            reorderedItem[key] = item[key];
          }
        });

        // Add the remaining keys
        otherItemKeys.forEach((key) => {
          reorderedItem[key] = item[key];
        });

        return reorderedItem;
      });

      return {
        ...prevItems,
        [category]: updatedCategoryItems,
      };
    });
  };

  const handleToggleVisibility = async (
    category,
    itemId,
    currentVisibility,
  ) => {
    // Default is visible if undefined
    const isVisible = currentVisibility !== false;
    const newVisibility = !isVisible;

    // Update local state
    const updatedItems = { ...items };
    const categoryItems = updatedItems[category];
    const itemIndex = categoryItems.findIndex((i) => i.id === itemId);

    if (itemIndex === -1) return;

    updatedItems[category][itemIndex] = {
      ...categoryItems[itemIndex],
      isVisible: newVisibility,
    };

    setItems(updatedItems);

    // Save to backend
    try {
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, updatedItems);
      } else {
        await updateCurrentCompanyItems(updatedItems);
      }
    } catch (err) {
      console.error("Error updating visibility:", err);
      setError(
        "Er is een fout opgetreden bij het bijwerken van de zichtbaarheid.",
      );
      // Revert on error
      updatedItems[category][itemIndex] = {
        ...categoryItems[itemIndex],
        isVisible: isVisible,
      };
      setItems(updatedItems);
    }
  };

  const handleDeleteAllItems = async () => {
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      await Promise.all(
        Object.values(items).flatMap((categoryItems) =>
          categoryItems.flatMap((item) =>
            extractImageIds(item).map((imageId) => cleanupImage(imageId)),
          ),
        ),
      );

      if (targetCompanyId !== null) {
        await deleteItemsByCompanyId(targetCompanyId);
      } else {
        await deleteCurrentCompanyItems();
      }
      setItems({});
      setActiveTab(null);
      setEditedItems(null);
      setSuccess("Alle items zijn succesvol verwijderd!");
    } catch (err) {
      console.error("Error deleting items:", err);
      setError("Er is een fout opgetreden bij het verwijderen van uw items.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (editedItems && editedItems.category === category) {
      setEditedItems(null);
    }

    // Clean up all images in this category before deleting
    console.log("Cleaning up images in category:", category);
    if (items[category]) {
      items[category].forEach((item) => {
        const imageIds = extractImageIds(item);
        imageIds.forEach((imageId) => cleanupImage(imageId));
      });
    }

    const newItems = { ...items };
    delete newItems[category];

    const categories = Object.keys(newItems);
    if (categories.length > 0) {
      setActiveTab(categories[0]);
    } else {
      setActiveTab(null);
    }

    setItems(newItems);

    try {
      setSaving(true);
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, newItems);
      } else {
        await updateCurrentCompanyItems(newItems);
      }
      setSuccess("Categorie verwijderd");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Error deleting category:", err);
      setError(
        "Er is een fout opgetreden bij het verwijderen van de categorie.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (category, itemId) => {
    if (
      editedItems &&
      editedItems.category === category &&
      editedItems.item.id === itemId
    ) {
      setEditedItems(null);
    }

    // Find the item to delete and clean up its images
    const itemToDelete = items[category]?.find((item) => item.id === itemId);

    if (itemToDelete) {
      console.log("Cleaning up images for deleted item:", itemToDelete.name);
      const imageIds = extractImageIds(itemToDelete);

      if (imageIds.length > 0) {
        console.log("Found", imageIds.length, "images to cleanup:", imageIds);
        imageIds.forEach((imageId) => cleanupImage(imageId));
      }
    }

    const newItems = { ...items };
    newItems[category] = newItems[category].filter(
      (item) => item.id !== itemId,
    );

    setItems(newItems);

    try {
      setSaving(true);
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, newItems);
      } else {
        await updateCurrentCompanyItems(newItems);
      }
      setSuccess("Item verwijderd");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Error deleting item:", err);
      setError("Er is een fout opgetreden bij het verwijderen van het item.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = (field) => {
    if (!editedItems) return;

    const { category, item } = editedItems;
    const imageIdsToDelete = extractImageIds({ [field]: item[field] });

    // Create a confirmation dialog to prevent accidental deletion
    setConfirmDialogTitle("Veld verwijderen");
    setConfirmDialogMessage(
      `Weet je zeker dat je het veld "${getFieldDisplayName(
        field,
      )}" wilt verwijderen voor alle items in de categorie "${category}"? Dit kan niet ongedaan worden gemaakt.`,
    );
    setConfirmDialogAction(() => async () => {
      setSaving(true);
      try {
        // Delete associated images first
        for (const imageId of imageIdsToDelete) {
          await cleanupImage(imageId);
        }

        // Create a new set of items with the field removed
        const updatedItems = (items[category] || []).map((i) => {
          const { [field]: _, ...rest } = i;
          return rest;
        });

        // Update the state
        setItems((prev) => ({
          ...prev,
          [category]: updatedItems,
        }));

        // Also remove from the currently edited item if it's open
        setEditedItems((prev) => {
          if (!prev) return null;
          const { [field]: _, ...rest } = prev.item;
          return {
            ...prev,
            item: rest,
          };
        });

        setSuccess(
          `Veld "${getFieldDisplayName(field)}" succesvol verwijderd.`,
        );
      } catch (err) {
        setError(`Fout bij het verwijderen van het veld: ${err.message}`);
      } finally {
        setSaving(false);
        setConfirmDialogOpen(false);
      }
    });
    setConfirmDialogOpen(true);
  };

  // Helper to delete a single image from a field that might contain multiple images
  const handleDeleteImageFromField = async (field, imageIdToDelete) => {
    if (!editedItems) return;

    setSaving(true);
    try {
      // Delete the image from storage
      await cleanupImage(imageIdToDelete);

      // Update the edited item state
      setEditedItems((prev) => {
        const currentImageValue = prev.item[field];
        let currentArray = [];

        if (Array.isArray(currentImageValue)) {
          currentArray = currentImageValue;
        } else if (
          currentImageValue !== null &&
          currentImageValue !== undefined &&
          currentImageValue !== ""
        ) {
          // Handle single value (number or string)
          currentArray = [currentImageValue];
        }

        const newArray = currentArray.filter((id) => id !== imageIdToDelete);

        return {
          ...prev,
          item: {
            ...prev.item,
            [field]: newArray, // Always store as array (even if empty or one)
          },
        };
      });

      setSuccess("Afbeelding succesvol verwijderd.");
    } catch (err) {
      setError(`Fout bij het verwijderen van de afbeelding: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditField = async (field, value, isReorder = false) => {
    if (!editedItems) return;

    // If this is a reorder operation (drag-and-drop), just update the array directly
    if (isReorder) {
      setEditedItems((prev) => ({
        ...prev,
        item: { ...prev.item, [field]: value },
      }));
      return;
    }

    // Detect if this is a media field (image or video)
    const isImageField =
      field.endsWith("_image") ||
      field.toLowerCase().includes("afbeelding") ||
      field.toLowerCase().includes("image");
    const isVideoField =
      field.endsWith("_video") || field.toLowerCase().includes("video");
    const isDocumentField =
      field.endsWith("_document") || field.toLowerCase().includes("document");
    const isMediaField = isImageField || isVideoField || isDocumentField;

    if (isMediaField) {
      const filesArray = (() => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (value instanceof FileList) return Array.from(value);
        if (value instanceof File) return [value];
        if (typeof value === "string" && value.startsWith("data:image/")) {
          return [value];
        }
        return [];
      })();

      if (filesArray.length === 0) {
        setEditedItems((prev) => ({
          ...prev,
          item: { ...prev.item, [field]: value },
        }));
        return;
      }

      setSaving(true);
      setUploadError(null);
      try {
        const allowedImageTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/webp",
        ];
        const allowedVideoTypes = [
          "video/mp4",
          "video/webm",
          "video/ogg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-matroska",
        ];

        const newIds = [];
        const totalFiles = filesArray.length;

        for (let i = 0; i < filesArray.length; i++) {
          let fileOrData = filesArray[i];
          // HEIC/HEIF (iPhone-foto's) kan de browser niet decoderen; converteer
          // eerst naar JPEG zodat compressie, upload en weergave werken.
          if (fileOrData instanceof File) {
            try {
              fileOrData = await convertHeicIfNeeded(fileOrData);
            } catch (convErr) {
              console.error("HEIC-conversie mislukt:", convErr);
              setError(
                `Kon HEIC-bestand "${fileOrData.name}" niet converteren. Probeer het als JPG of PNG.`,
              );
              continue;
            }
          }
          const fileIsVideo =
            fileOrData instanceof File && fileOrData.type.startsWith("video/");
          const fileIsImage =
            fileOrData instanceof File && fileOrData.type.startsWith("image/");
          const fileIsDocument =
            fileOrData instanceof File &&
            fileOrData.type === "application/pdf";

          if (fileOrData instanceof File) {
            // Validate type
            if (fileIsVideo && !allowedVideoTypes.includes(fileOrData.type)) {
              console.warn(
                "Videobestandstype niet toegestaan:",
                fileOrData.type,
              );
              continue;
            }
            if (
              fileIsImage &&
              !allowedImageTypes.includes(fileOrData.type) &&
              !fileOrData.type.startsWith("image/")
            ) {
              console.warn("Bestandstype niet toegestaan:", fileOrData.type);
              continue;
            }
            // Only PDF documents are allowed for document fields
            if (isDocumentField && !fileIsDocument) {
              console.warn(
                "Documentveld accepteert enkel PDF:",
                fileOrData.type,
              );
              continue;
            }

            // Video's en PDF's gaan ongecomprimeerd via multipart (met progress).
            if (fileIsVideo || fileIsDocument) {
              setUploadProgress({
                current: i + 1,
                total: totalFiles,
                percent: 0,
                fileName: fileOrData.name,
              });

              const resp = await uploadMedia(fileOrData, (percent) => {
                setUploadProgress((prev) => ({ ...prev, percent }));
              });

              let mediaKey =
                resp?.data?.mediaKey ??
                resp?.data?.imageKey ??
                resp?.data?.id ??
                Date.now();
              mediaKey = stripDomain(mediaKey);
              newIds.push(mediaKey);
            } else {
              // Afbeeldingen: eerst comprimeren, daarna via multipart uploaden.
              // We gebruiken NIET langer de base64-route (/upload-image), die op
              // de server gecapt is (±2MB) en bij grotere foto's een 500 gaf.
              // Multipart heeft die limiet niet en geeft een echte voortgangsbalk.
              setUploadProgress({
                current: i + 1,
                total: totalFiles,
                percent: 0,
                fileName: fileOrData.name,
              });
              const dataUrl = await smartCompressImage(fileOrData);
              const compressedFile = dataUrlToFile(dataUrl, fileOrData.name);
              const resp = await uploadMedia(compressedFile, (percent) => {
                setUploadProgress((prev) => ({ ...prev, percent }));
              });
              let imageKey =
                resp?.data?.mediaKey ??
                resp?.data?.imageKey ??
                resp?.data?.imageId ??
                resp?.data?.id ??
                Date.now();
              imageKey = stripDomain(imageKey);
              newIds.push(imageKey);
            }
          } else if (
            typeof fileOrData === "string" &&
            fileOrData.startsWith("data:image")
          ) {
            setUploadProgress({
              current: i + 1,
              total: totalFiles,
              percent: 50,
              fileName: "image",
            });
            const resp = await uploadImage(fileOrData);
            let imageKey =
              resp?.data?.imageKey ??
              resp?.data?.imageId ??
              resp?.data?.id ??
              Date.now();
            imageKey = stripDomain(imageKey);
            newIds.push(imageKey);
          }
        }

        setUploadProgress(null);

        // Merge with existing
        setEditedItems((prev) => {
          const existing = prev.item[field];
          const existingArray = Array.isArray(existing)
            ? existing
            : (typeof existing === "number" && existing) ||
                (typeof existing === "string" &&
                  (existing.startsWith("images/") ||
                    existing.startsWith("videos/") ||
                    existing.startsWith("documents/")))
              ? [existing]
              : [];
          const merged = [...existingArray, ...newIds];
          return {
            ...prev,
            item: { ...prev.item, [field]: merged },
          };
        });
      } catch (err) {
        console.error(err);
        setUploadProgress(null);
        const status = err.response?.status;
        const serverMsg = err.response?.data?.message;
        let friendly;
        if (status === 413 || /too large|payload|limit/i.test(err.message || "")) {
          friendly =
            "De foto is te groot voor de server. Probeer een kleinere afbeelding.";
        } else if (status >= 500) {
          friendly =
            "De server kon de upload niet verwerken (500). Probeer het opnieuw of gebruik een kleinere/ander formaat foto.";
        } else {
          friendly = serverMsg || err.message || "Onbekende fout";
        }
        setUploadError(friendly);
        setError(`Fout bij het uploaden: ${friendly}`);
      } finally {
        setSaving(false);
      }
    } else {
      // For non-media fields, just update the value
      setEditedItems((prev) => ({
        ...prev,
        item: { ...prev.item, [field]: value },
      }));
    }
  };

  const isItemBeingEdited = (category, itemId) => {
    return (
      editedItems &&
      editedItems.category === category &&
      editedItems.item.id === itemId
    );
  };

  const isAddingNewItem = (category) => {
    return (
      editedItems &&
      editedItems.category === category &&
      !items[category].some((item) => item.id === editedItems.item.id)
    );
  };

  const getFilteredItems = (category) => {
    if (!searchQuery.trim()) return items[category] || [];

    const query = searchQuery.toLowerCase().trim();

    return (items[category] || []).filter((item) => {
      // Search across all fields
      return Object.entries(item).some(([key, value]) => {
        if (key === "id" || key === "_fieldMetadata" || key === "_translations")
          return false;
        if (value === null || value === undefined) return false;

        // Handle arrays (e.g. array fields)
        if (Array.isArray(value)) {
          return value.some((v) => {
            const dv = getDisplayValue(v);
            return dv != null && String(dv).toLowerCase().includes(query);
          });
        }

        // Handle translation objects - extract display value
        const displayValue = getDisplayValue(value);
        if (displayValue === null || displayValue === undefined) return false;

        // Skip objects that aren't translations (image objects, etc.)
        if (typeof displayValue === "object") return false;

        return String(displayValue).toLowerCase().includes(query);
      });
    });
  };

  // Memoize filtered+sorted items per category so the table always reflects latest searchQuery
  const filteredItemsByCategory = useMemo(() => {
    const result = {};
    Object.keys(items).forEach((category) => {
      result[category] = getFilteredItems(category);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchQuery]);

  // Helper function to detect if a field is a number/rank field
  const isNumberField = (fieldName) => {
    const lowerField = fieldName.toLowerCase();
    console.log("isNumberField", lowerField);
    return (
      lowerField === "volgnummer_number" || // Only specific fields
      lowerField === "nummer_number" ||
      lowerField === "rank_number" ||
      lowerField === "order_number" ||
      lowerField === "sequence_number" ||
      lowerField === "position_number" ||
      lowerField === "index_number"
    );
  };

  // Helper function to get the number value for sorting
  const getNumberValue = (item, numberField) => {
    const value = item[numberField];
    if (value === null || value === undefined || value === "") return 0;
    console.log("getNumberValue for field", numberField, "value:", value);
    // Try to convert to number
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Get sorted items for a category
  const getSortedItems = (category) => {
    const filteredItems = filteredItemsByCategory[category] || [];

    // Prefer a stable rank field if available
    const fields = categoryFields[category] || [];
    const preferredOrderFields = [
      "rank_number",
      "volgnummer_number",
      "order_number",
      "position_number",
      "sequence_number",
      "index_number",
      "nummer_number",
    ];
    const preferredField = preferredOrderFields.find((f) => fields.includes(f));

    // Fallback to any recognized number/rank field
    const numberField = preferredField || fields.find((f) => isNumberField(f));

    console.log("Category:", category, "Number field used:", numberField);
    console.log("Category fields:", categoryFields[category]);

    if (numberField) {
      console.log("Sorting by number field:", numberField);
      // Sort by the number field
      return [...filteredItems].sort((a, b) => {
        const aValue = getNumberValue(a, numberField);
        const bValue = getNumberValue(b, numberField);
        return aValue - bValue;
      });
    }

    // If no number field found, return as is (array order)
    console.log("No number field found, returning unsorted");
    return filteredItems;
  };

  // Reorder helpers for drag & drop
  const reorderItemsInCategory = (category, draggedId, targetId) => {
    const currentList = items[category] ? [...items[category]] : [];
    const fromIdx = currentList.findIndex((i) => i.id === draggedId);
    const toIdx = currentList.findIndex((i) => i.id === targetId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return null;

    const [moved] = currentList.splice(fromIdx, 1);
    currentList.splice(toIdx, 0, moved);

    // If there's a number ranking field for this category, update it to reflect new order
    const numberField = categoryFields[category]?.find((f) => isNumberField(f));
    const finalList = numberField
      ? currentList.map((it, idx) => ({ ...it, [numberField]: idx + 1 }))
      : currentList;

    return finalList;
  };

  const onRowDragStart = (category, item) => (e) => {
    try {
      e.dataTransfer.setData("text/plain", String(item.id));
      e.dataTransfer.effectAllowed = "move";
    } catch {}
  };

  const onRowDragOver = (e) => {
    // Allow dropping by preventing default
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = "move";
    } catch {}
  };

  const onRowDrop = (category, targetItem) => async (e) => {
    e.preventDefault();
    let draggedIdRaw = undefined;
    try {
      draggedIdRaw = e.dataTransfer.getData("text/plain");
    } catch {}
    const draggedId = Number(draggedIdRaw);
    if (!draggedId || draggedId === targetItem.id) return;

    // Reorder according to the CURRENTLY DISPLAYED order to match what the user sees
    const displayedList = getSortedItems(category);
    const fromIdx = displayedList.findIndex((i) => i.id === draggedId);
    const toIdx = displayedList.findIndex((i) => i.id === targetItem.id);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const newDisplayOrder = [...displayedList];
    const [moved] = newDisplayOrder.splice(fromIdx, 1);
    newDisplayOrder.splice(toIdx, 0, moved);

    // Determine or create a ranking field to make the order persistent across reloads
    const fields = categoryFields[category] || [];
    const preferredOrderFields = [
      "rank_number",
      "volgnummer_number",
      "order_number",
      "position_number",
      "sequence_number",
      "index_number",
      "nummer_number",
    ];
    const numberField =
      preferredOrderFields.find((f) => fields.includes(f)) ||
      fields.find((f) => isNumberField(f)) ||
      "rank_number"; // create if missing

    // Build the updated category list with rank values matching the new display order
    const updatedCategoryItems = newDisplayOrder.map((it, idx) => ({
      ...it,
      [numberField]: idx + 1,
    }));

    const newItems = { ...items, [category]: updatedCategoryItems };
    setItems(newItems);

    try {
      setSaving(true);
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, newItems);
      } else {
        await updateCurrentCompanyItems(newItems);
      }
      setSuccess("Volgorde opgeslagen");
      setTimeout(() => setSuccess(""), 1200);
    } catch (err) {
      console.error("Error saving new order:", err);
      setError("Kon nieuwe volgorde niet opslaan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (!activeTab || !items[activeTab]) return;

    const currentItems = items[activeTab];

    // Helper to check if a key is an image field
    const isImageField = (key) => /image|afbeelding|foto|picture/i.test(key);

    // Get all unique keys from all items to ensure we have all columns
    const allKeys = new Set();
    // Always include id and name first
    allKeys.add("id");
    allKeys.add("name");

    // Also add keys from categoryFields to ensure we have all known fields even if empty in items
    if (categoryFields[activeTab]) {
      categoryFields[activeTab].forEach((k) => {
        if (!isImageField(k)) {
          allKeys.add(k);
        }
      });
    }

    currentItems.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (
          !["_fieldMetadata", "_translations"].includes(key) &&
          !isImageField(key)
        ) {
          allKeys.add(key);
        }
      });
    });

    // Convert to array of objects with all keys
    const data = currentItems.map((item) => {
      const row = {};
      Array.from(allKeys).forEach((key) => {
        row[key] = item[key] || "";
      });
      return row;
    });

    let ws;
    if (data.length === 0) {
      ws = XLSX.utils.aoa_to_sheet([Array.from(allKeys)]);
    } else {
      ws = XLSX.utils.json_to_sheet(data, { header: Array.from(allKeys) });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab);
    XLSX.writeFile(wb, `${activeTab}_items.xlsx`);
  };

  const handleUploadItems = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (!data || data.length === 0) {
          setError("Geen data gevonden in het bestand");
          return;
        }

        const currentItems = [...(items[activeTab] || [])];
        // Create a map for faster lookup by ID
        const newItemsMap = new Map(currentItems.map((i) => [String(i.id), i]));

        // Helper to check if a key is an image field
        const isImageField = (key) =>
          /image|afbeelding|foto|picture/i.test(key);

        data.forEach((row, index) => {
          // If row has ID, update existing or add new with that ID
          // If row has no ID, generate one
          let itemToSave = { ...row };

          // Clean up empty keys and remove image fields from import
          Object.keys(itemToSave).forEach((key) => {
            if (
              itemToSave[key] === undefined ||
              itemToSave[key] === null ||
              isImageField(key)
            ) {
              delete itemToSave[key];
            }
          });

          if (itemToSave.id) {
            const idStr = String(itemToSave.id);
            // Update existing
            if (newItemsMap.has(idStr)) {
              const existing = newItemsMap.get(idStr);
              // Merge existing with new, preserving existing images because we deleted image keys from itemToSave
              newItemsMap.set(idStr, { ...existing, ...itemToSave });
            } else {
              // Add new with specific ID
              newItemsMap.set(idStr, itemToSave);
            }
          } else {
            // New item without ID
            const newId = Date.now() + index; // Add index to avoid collision in same batch
            itemToSave.id = newId;
            newItemsMap.set(String(newId), itemToSave);
          }
        });

        const updatedList = Array.from(newItemsMap.values());

        const newItems = {
          ...items,
          [activeTab]: updatedList,
        };

        setItems(newItems);

        setSaving(true);
        if (targetCompanyId !== null) {
          await updateItemsByCompanyId(targetCompanyId, newItems);
        } else {
          await updateCurrentCompanyItems(newItems);
        }
        setSuccess("Items succesvol geïmporteerd");
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        console.error("Error importing items:", err);
        setError("Fout bij importeren van items");
      } finally {
        setSaving(false);
        // Reset file input
        e.target.value = "";
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = (items[activeTab] || []).map((item) => item.id);
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id, checked) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;

    setConfirmDialogTitle("Geselecteerde items verwijderen");
    setConfirmDialogMessage(
      `Weet u zeker dat u ${selectedItems.size} geselecteerde item(s) wilt verwijderen?`,
    );
    setConfirmDialogAction(() => async () => {
      const newItems = { ...items };
      newItems[activeTab] = newItems[activeTab].filter(
        (item) => !selectedItems.has(item.id),
      );

      setItems(newItems);
      setSelectedItems(new Set());
      setConfirmDialogOpen(false);

      try {
        setSaving(true);
        if (targetCompanyId !== null) {
          await updateItemsByCompanyId(targetCompanyId, newItems);
        } else {
          await updateCurrentCompanyItems(newItems);
        }
        setSuccess("Geselecteerde items verwijderd");
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        console.error("Error deleting selected items:", err);
        setError("Er is een fout opgetreden bij het verwijderen van de items.");
      } finally {
        setSaving(false);
      }
    });
    setConfirmDialogOpen(true);
  };

  // --- Category-wide auto-translate ---
  const LANG_NAMES = {
    nl: "Nederlands",
    fr: "Frans",
    en: "Engels",
    de: "Duits",
    es: "Spaans",
    it: "Italiaans",
    pt: "Portugees",
  };

  // Free Google translate endpoint (same as the per-item translator in ItemEditor)
  const translateText = async (text, sourceLang, targetLang) => {
    if (!text) return text;
    try {
      const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(
        text,
      )}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data && Array.isArray(data[0])) {
        return data[0].map((seg) => (seg && seg[0] ? seg[0] : "")).join("");
      }
      return text;
    } catch (error) {
      console.error("Translation API error:", error);
      return text;
    }
  };

  // Only translate human-readable text fields; never media / select / numeric / structural fields.
  const shouldFieldBeTranslated = (fieldName, value) => {
    if (
      ["id", "name", "_fieldMetadata", "_translations", "isVisible"].includes(
        fieldName,
      )
    ) {
      return fieldName === "name";
    }
    if (/image|afbeelding|foto|picture|video|document/i.test(fieldName))
      return false;
    if (/_select/.test(fieldName)) return false;
    if (/_unit$|_check$|_number$|_date$/.test(fieldName)) return false;
    const type = detectFieldType(fieldName, value);
    return type === "text" || type === "textarea" || type === "array";
  };

  // Languages already configured anywhere in the category (falls back to nl/fr/en).
  const getCategoryLanguages = (category) => {
    const langs = new Set();
    (items[category] || []).forEach((it) => {
      (it._translations?.languages || []).forEach((l) => langs.add(l));
    });
    if (langs.size === 0) ["nl", "fr", "en"].forEach((l) => langs.add(l));
    return Array.from(langs);
  };

  const openTranslateCategoryDialog = () => {
    if (!activeTab) return;
    const langs = getCategoryLanguages(activeTab);
    const src = langs.includes("nl") ? "nl" : langs[0];
    setTranslateSourceLang(src);
    setTranslateTargetLangs(langs.filter((l) => l !== src));
    setTranslateProgress({ current: 0, total: (items[activeTab] || []).length });
    setTranslateCategoryDialogOpen(true);
  };

  const toggleTranslateTargetLang = (lang) => {
    setTranslateTargetLangs((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );
  };

  // Translate every item in the active category in one pass.
  const handleAutoTranslateCategory = async () => {
    const category = activeTab;
    const sourceLang = translateSourceLang;
    const targetLangs = translateTargetLangs.filter((l) => l !== sourceLang);
    if (!category || targetLangs.length === 0) return;

    const allLangs = Array.from(new Set([sourceLang, ...targetLangs]));
    const sourceItems = items[category] || [];

    setError("");
    setSuccess("");
    setIsTranslatingCategory(true);
    setTranslateProgress({ current: 0, total: sourceItems.length });

    try {
      const newCategoryItems = [];

      for (let idx = 0; idx < sourceItems.length; idx++) {
        const item = { ...sourceItems[idx] };
        const existingLangs = item._translations?.languages || [];
        const languages = Array.from(new Set([...existingLangs, ...allLangs]));

        for (const field of Object.keys(item)) {
          if (!shouldFieldBeTranslated(field, item[field])) continue;
          const value = item[field];

          if (Array.isArray(value)) {
            const newArr = [];
            for (const el of value) {
              const obj =
                typeof el === "object" && el !== null && !Array.isArray(el)
                  ? { ...el }
                  : { [sourceLang]: el ?? "" };
              const sourceText = obj[sourceLang] || "";
              if (sourceText) {
                for (const t of targetLangs) {
                  if (translateOverwrite || !obj[t]) {
                    obj[t] = await translateText(
                      String(sourceText),
                      sourceLang,
                      t,
                    );
                  }
                }
              }
              newArr.push(obj);
            }
            item[field] = newArr;
          } else {
            const obj =
              typeof value === "object" && value !== null
                ? { ...value }
                : { [sourceLang]: value ?? "" };
            const sourceText = obj[sourceLang] || "";
            if (sourceText) {
              for (const t of targetLangs) {
                if (translateOverwrite || !obj[t]) {
                  obj[t] = await translateText(
                    String(sourceText),
                    sourceLang,
                    t,
                  );
                }
              }
            }
            item[field] = obj;
          }
        }

        item._translations = { enabled: true, languages };
        newCategoryItems.push(item);
        setTranslateProgress({ current: idx + 1, total: sourceItems.length });
      }

      const newItems = { ...items, [category]: newCategoryItems };
      setItems(newItems);

      setSaving(true);
      if (targetCompanyId !== null) {
        await updateItemsByCompanyId(targetCompanyId, newItems);
      } else {
        await updateCurrentCompanyItems(newItems);
      }
      setSuccess(
        `Categorie "${category}" vertaald (${newCategoryItems.length} items).`,
      );
      setTimeout(() => setSuccess(""), 2500);
      setTranslateCategoryDialogOpen(false);
    } catch (err) {
      console.error("Error translating category:", err);
      setError("Er is een fout opgetreden bij het vertalen van de categorie.");
    } finally {
      setIsTranslatingCategory(false);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Item Editor Component */}
      <ItemEditor
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditDialogOpen(false);
            setEditedItems(null);
            setUploadError(null);
          }
        }}
        editedItems={editedItems}
        isAddingNewItem={isAddingNewItem}
        onSave={handleFinishEditing}
        onCancel={handleCancelEditing}
        onDeleteField={handleDeleteField}
        onAddField={handleAddField}
        onEditField={handleEditField}
        onReorderFields={handleReorderEditedItemFields}
        onChangeFieldType={handleChangeFieldType}
        onConvertSelectToCategory={handleConvertSelectToCategory}
        onDeleteImage={handleDeleteImageFromField}
        existingCategories={Object.keys(items)}
        allItems={items}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
        onDismissUploadError={() => setUploadError(null)}
      />

      <div className="grid grid-cols-1 gap-6">
        {/* Header section with stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Layers
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              />
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Categorieën
              </span>
            </div>
            <div
              className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {Object.keys(items).length}
            </div>
          </div>

          <div
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Tag
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              />
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Totaal items
              </span>
            </div>
            <div
              className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {totalItems}
            </div>
          </div>

          <div
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Settings
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              />
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Status
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-500" />
              <span
                className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Actief
              </span>
            </div>
          </div>
        </div>

        <motion.div
          className="flex flex-col gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header with title and primary actions */}
          <div className="flex justify-between items-center">
            <h1
              className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {targetCompanyId ? "Items beheren (Admin)" : "Items beheren"}
            </h1>

            <div className="flex gap-2 items-center">
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    className={`h-8 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Categorie
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
                >
                  <DialogHeader>
                    <DialogTitle
                      className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Nieuwe categorie
                    </DialogTitle>
                    <DialogDescription
                      className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Voer een naam in voor de nieuwe categorie.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-3">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Categorienaam"
                      className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddCategory();
                        }
                      }}
                    />
                  </div>
                  <DialogFooter className="gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAddDialogOpen(false)}
                      className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      Annuleren
                    </Button>
                    <Button
                      onClick={handleAddCategory}
                      className={`h-9 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                      disabled={!newCategoryName.trim()}
                    >
                      Toevoegen
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {activeTab && (
                <>
                  {selectedItems.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteSelected}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-lg h-8 text-[12px] mr-1"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Verwijder ({selectedItems.size})
                    </Button>
                  )}
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: "none" }}
                    id="excel-upload"
                    onChange={handleUploadItems}
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-8 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                      >
                        <Settings className="h-3.5 w-3.5 mr-1.5" />
                        Acties
                        <ChevronDown className="h-3 w-3 ml-1.5 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className={`w-48 rounded-lg ${isDark ? "bg-[#1a1a1a] border-white/[0.08]" : "border-gray-200"}`}
                    >
                      <DropdownMenuLabel
                        className={`text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Categorie opties
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator
                        className={isDark ? "bg-white/[0.06]" : ""}
                      />

                      <DropdownMenuItem
                        onClick={() => handleAddItem(activeTab)}
                        className={`text-[12px] cursor-pointer ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Item toevoegen
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() =>
                          document.getElementById("excel-upload").click()
                        }
                        className={`text-[12px] cursor-pointer ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                      >
                        <PackagePlus className="h-3.5 w-3.5 mr-1.5" /> Upload
                        items
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={handleDownloadTemplate}
                        className={`text-[12px] cursor-pointer ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                        Template
                      </DropdownMenuItem>

                      <DropdownMenuSeparator
                        className={isDark ? "bg-white/[0.06]" : ""}
                      />

                      <DropdownMenuItem
                        onClick={openTranslateCategoryDialog}
                        className={`text-[12px] cursor-pointer ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                      >
                        <Languages className="h-3.5 w-3.5 mr-1.5" /> Auto-vertaal
                        categorie
                      </DropdownMenuItem>

                      <DropdownMenuSeparator
                        className={isDark ? "bg-white/[0.06]" : ""}
                      />

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            className="text-red-500 text-[12px] cursor-pointer"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Categorie
                            verwijderen
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                          className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-red-500 text-[14px] font-semibold">
                              Categorie verwijderen
                            </AlertDialogTitle>
                            <AlertDialogDescription
                              className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              Weet u zeker dat u de categorie "{activeTab}" wilt
                              verwijderen? Alle items worden permanent
                              verwijderd.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter className="gap-2">
                            <AlertDialogCancel
                              className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                            >
                              Annuleren
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteCategory(activeTab)}
                              className="bg-red-600 hover:bg-red-700 text-white h-9 rounded-lg text-[12px]"
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {/* Notifications */}
          {error && (
            <div
              className={`px-4 py-3 rounded-lg text-[12px] font-medium flex items-center gap-2 ${isDark ? "bg-red-500/10 border border-red-500/15 text-red-400" : "bg-red-50 border border-red-100 text-red-700"}`}
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div
              className={`px-4 py-3 rounded-lg text-[12px] font-medium flex items-center gap-2 ${isDark ? "bg-emerald-500/10 border border-emerald-500/15 text-emerald-400" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}
            >
              <Check className="h-3.5 w-3.5 shrink-0" />
              {success}
            </div>
          )}

          {saving && (
            <div
              className={`px-4 py-3 rounded-lg text-[12px] font-medium flex items-center gap-2 ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-gray-400" : "bg-gray-50 border border-gray-100 text-gray-600"}`}
            >
              <div
                className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${isDark ? "border-white/10 border-t-white/40" : "border-gray-200 border-t-gray-500"}`}
              />
              Bezig met opslaan...
            </div>
          )}
        </motion.div>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent
            className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
          >
            <DialogHeader>
              <DialogTitle
                className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {confirmDialogTitle}
              </DialogTitle>
              <DialogDescription
                className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {confirmDialogMessage}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDialogOpen(false)}
                className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </Button>
              <Button
                onClick={confirmDialogAction}
                className={`h-9 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                Doorgaan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category-wide auto-translate dialog */}
        <Dialog
          open={translateCategoryDialogOpen}
          onOpenChange={(open) => {
            if (!isTranslatingCategory) setTranslateCategoryDialogOpen(open);
          }}
        >
          <DialogContent
            className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
          >
            <DialogHeader>
              <DialogTitle
                className={`text-[14px] font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
              >
                <Languages className="h-4 w-4" />
                Categorie automatisch vertalen
              </DialogTitle>
              <DialogDescription
                className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Vertaalt alle tekstvelden van elk item in "{activeTab}" (
                {(items[activeTab] || []).length} items) naar de gekozen talen.
              </DialogDescription>
            </DialogHeader>

            {(() => {
              const langs = getCategoryLanguages(activeTab);
              return (
                <div className="py-2 space-y-4">
                  {/* Source language */}
                  <div>
                    <div
                      className={`text-[12px] font-medium mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Bronentaal (vertalen vanuit)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {langs.map((lang) => {
                        const active = translateSourceLang === lang;
                        return (
                          <button
                            key={lang}
                            type="button"
                            disabled={isTranslatingCategory}
                            onClick={() => {
                              setTranslateSourceLang(lang);
                              setTranslateTargetLangs((prev) =>
                                prev.filter((l) => l !== lang),
                              );
                            }}
                            className={`px-2.5 h-7 rounded-lg text-[12px] font-medium border transition ${
                              active
                                ? isDark
                                  ? "bg-white text-black border-white"
                                  : "bg-gray-900 text-white border-gray-900"
                                : isDark
                                  ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]"
                                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {LANG_NAMES[lang] || lang.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Target languages */}
                  <div>
                    <div
                      className={`text-[12px] font-medium mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Doeltalen (vertalen naar)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {langs
                        .filter((l) => l !== translateSourceLang)
                        .map((lang) => {
                          const active = translateTargetLangs.includes(lang);
                          return (
                            <button
                              key={lang}
                              type="button"
                              disabled={isTranslatingCategory}
                              onClick={() => toggleTranslateTargetLang(lang)}
                              className={`px-2.5 h-7 rounded-lg text-[12px] font-medium border transition ${
                                active
                                  ? isDark
                                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : isDark
                                    ? "border-white/[0.08] text-gray-500 hover:bg-white/[0.04]"
                                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {LANG_NAMES[lang] || lang.toUpperCase()}
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  {/* Overwrite toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={translateOverwrite}
                      onCheckedChange={(c) => setTranslateOverwrite(!!c)}
                      disabled={isTranslatingCategory}
                    />
                    <span
                      className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Bestaande vertalingen overschrijven
                    </span>
                  </label>

                  {/* Progress */}
                  {isTranslatingCategory && (
                    <div
                      className={`px-3 py-2.5 rounded-lg text-[12px] flex items-center gap-2 ${isDark ? "bg-white/[0.04] text-gray-300" : "bg-gray-50 text-gray-600"}`}
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Bezig met vertalen... {translateProgress.current}/
                      {translateProgress.total} items
                    </div>
                  )}
                </div>
              );
            })()}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isTranslatingCategory}
                onClick={() => setTranslateCategoryDialogOpen(false)}
                className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </Button>
              <Button
                onClick={handleAutoTranslateCategory}
                disabled={
                  isTranslatingCategory ||
                  translateTargetLangs.length === 0 ||
                  (items[activeTab] || []).length === 0
                }
                className={`h-9 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                {isTranslatingCategory ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Vertalen...
                  </>
                ) : (
                  <>
                    <Languages className="h-3.5 w-3.5 mr-1.5" />
                    Vertaal categorie
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {Object.keys(items).length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div className="text-center py-14 px-4">
                <div
                  className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                >
                  <PackagePlus
                    className={`h-6 w-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  />
                </div>
                <h3
                  className={`text-[14px] font-semibold mb-1.5 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Nog geen items
                </h3>
                <p
                  className={`text-[13px] mb-6 max-w-md mx-auto ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Maak uw eerste categorie om items toe te voegen.
                </p>
                <Button
                  onClick={() => setAddDialogOpen(true)}
                  className={`h-9 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Eerste categorie
                </Button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`sm:rounded-xl sm:overflow-hidden ${isDark ? "sm:bg-white/[0.03] sm:border sm:border-white/[0.06]" : "sm:bg-white sm:border sm:border-gray-200/80"}`}
            >
              <Tabs
                value={activeTab}
                onValueChange={(tab) => {
                  if (editedItems && tab !== editedItems.category) {
                    setConfirmDialogTitle("Niet-opgeslagen wijzigingen");
                    setConfirmDialogMessage(
                      "U heeft niet-opgeslagen wijzigingen. Weet u zeker dat u wilt switchen?",
                    );
                    setConfirmDialogAction(() => () => {
                      setActiveTab(tab);
                      setEditedItems(null);
                      setSearchQuery("");
                      setConfirmDialogOpen(false);
                    });
                    setConfirmDialogOpen(true);
                  } else {
                    setActiveTab(tab);
                    setSearchQuery("");
                  }
                }}
                className="w-full"
              >
                <div
                  className={`rounded-xl overflow-hidden mb-3 sm:rounded-none sm:overflow-visible sm:mb-0 border sm:border-0 sm:border-b ${isDark ? "bg-white/[0.03] border-white/[0.06] sm:bg-transparent" : "bg-white border-gray-200/80 sm:bg-transparent sm:border-gray-100"}`}
                >
                  <TabsList className="h-10 bg-transparent border-0 p-0 w-full overflow-x-auto justify-start gap-0">
                    {Object.keys(items).map((category) => (
                      <TabsTrigger
                        key={category}
                        value={category}
                        className={`h-10 px-4 text-[12px] font-medium border-b-2 border-transparent rounded-none transition-colors
                          ${
                            isDark
                              ? "text-gray-500 data-[state=active]:border-white data-[state=active]:text-white data-[state=active]:bg-transparent"
                              : "text-gray-500 data-[state=active]:border-gray-900 data-[state=active]:text-gray-900 data-[state=active]:bg-transparent"
                          } data-[state=active]:shadow-none`}
                      >
                        <div className="flex items-center">
                          <span>{category}</span>
                          <span
                            className={`ml-2 text-[11px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-500"}`}
                          >
                            {items[category].length}
                          </span>
                        </div>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {/* Search & Filter Bar */}
                {activeTab && (
                  <div
                    className={`p-3 flex flex-wrap gap-2 rounded-xl mb-3 sm:rounded-none sm:mb-0 border sm:border-0 sm:border-b ${isDark ? "bg-white/[0.03] border-white/[0.06] sm:border-white/[0.04] sm:bg-white/[0.01]" : "bg-white border-gray-200/80 sm:border-gray-50 sm:bg-gray-50/30"}`}
                  >
                    <div className="relative flex-1 min-w-[200px]">
                      <Search
                        className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      />
                      <Input
                        placeholder="Zoek in items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`pl-9 ${searchQuery ? "pr-8" : "pr-3"} h-8 rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAddItem(activeTab)}
                        size="sm"
                        className={`h-8 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Item toevoegen
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tab content */}
                {Object.keys(items).map((category) => (
                  <TabsContent
                    key={category}
                    value={category}
                    className="p-0 m-0"
                  >
                    {items[category].length === 0 ? (
                      <div className="text-center py-10 px-4">
                        <div
                          className={`w-10 h-10 mx-auto mb-3 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                        >
                          <PackagePlus
                            className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                        </div>
                        <p
                          className={`text-[13px] mb-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Deze categorie bevat nog geen items.
                        </p>
                        <Button
                          onClick={() => handleAddItem(category)}
                          size="sm"
                          className={`h-8 rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                        >
                          <Plus className="mr-1.5 h-3.5 w-3.5" /> Item toevoegen
                        </Button>
                      </div>
                    ) : (
                      <div>
                        {/* Table of existing items */}
                        {getSortedItems(category).length > 0 ? (
                          <>
                            {/* ── Mobile card layout ── */}
                            <div className="sm:hidden space-y-3 pt-1">
                              {getSortedItems(category).map((item, i) => {
                                const imageData = findImageField(item);
                                let imgSrc = null;
                                if (
                                  imageData?.value &&
                                  typeof imageData.value === "string" &&
                                  imageData.value.startsWith("images")
                                ) {
                                  imgSrc = S3_BASE_URL + "/" + imageData.value;
                                }

                                return (
                                  <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.02 }}
                                    className={`rounded-xl overflow-hidden ${
                                      item.isVisible === false
                                        ? isDark
                                          ? "opacity-50"
                                          : "opacity-60 grayscale-[0.3]"
                                        : ""
                                    } ${
                                      isDark
                                        ? "bg-white/[0.03] border border-white/[0.06]"
                                        : "bg-white border border-gray-200/80"
                                    }`}
                                  >
                                    <div className="p-3">
                                      <div className="flex items-center gap-3">
                                        {/* Image */}
                                        {imgSrc ? (
                                          <img
                                            src={imgSrc}
                                            alt="Item"
                                            className="h-11 w-11 rounded-lg object-cover flex-shrink-0"
                                            loading="lazy"
                                          />
                                        ) : (
                                          <div
                                            className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                                          >
                                            <PackagePlus
                                              className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                            />
                                          </div>
                                        )}

                                        {/* Name */}
                                        <div className="flex-1 min-w-0">
                                          <p
                                            className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                                          >
                                            {getDisplayValue(item.name)}
                                          </p>
                                          {item.isVisible === false && (
                                            <span
                                              className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                            >
                                              <EyeOff className="h-2.5 w-2.5" />
                                              Verborgen
                                            </span>
                                          )}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                          <button
                                            onClick={() =>
                                              handleStartEditing(category, item)
                                            }
                                            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                                          >
                                            <Pencil className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleToggleVisibility(
                                                category,
                                                item.id,
                                                item.isVisible,
                                              )
                                            }
                                            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 active:bg-white/[0.06]" : "text-gray-400 active:bg-gray-100"}`}
                                          >
                                            {item.isVisible !== false ? (
                                              <Eye className="h-3.5 w-3.5" />
                                            ) : (
                                              <EyeOff className="h-3.5 w-3.5" />
                                            )}
                                          </button>
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <button
                                                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 active:bg-red-500/15" : "text-gray-400 active:bg-red-50"}`}
                                              >
                                                <Trash2 className="h-3.5 w-3.5" />
                                              </button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent
                                              className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
                                            >
                                              <AlertDialogHeader>
                                                <AlertDialogTitle className="text-red-500 text-[14px] font-semibold">
                                                  Item verwijderen
                                                </AlertDialogTitle>
                                                <AlertDialogDescription
                                                  className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                                >
                                                  Weet u zeker dat u dit item
                                                  wilt verwijderen? Deze actie
                                                  kan niet ongedaan worden
                                                  gemaakt.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter className="gap-2">
                                                <AlertDialogCancel
                                                  className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                                                >
                                                  Annuleren
                                                </AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() =>
                                                    handleDeleteItem(
                                                      category,
                                                      item.id,
                                                    )
                                                  }
                                                  className="bg-red-600 hover:bg-red-700 text-white h-9 rounded-lg text-[12px]"
                                                >
                                                  Verwijderen
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>

                            {/* ── Desktop table layout ── */}
                            <div className="hidden sm:block rounded-md overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow
                                    className={`hover:bg-transparent ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/50 border-gray-100"}`}
                                  >
                                    {/* Checkbox column */}
                                    <TableHead className="w-[48px] p-0 pl-2">
                                      <Checkbox
                                        checked={
                                          items[category].length > 0 &&
                                          selectedItems.size ===
                                            items[category].length
                                        }
                                        onCheckedChange={(checked) =>
                                          handleSelectAll(!!checked)
                                        }
                                      />
                                    </TableHead>
                                    {/* Drag handle column */}
                                    <TableHead className="w-[32px] p-0"></TableHead>
                                    {/* Check if any item in this category has an image field */}
                                    {getSortedItems(category).some((item) =>
                                      findImageField(item),
                                    ) && (
                                      <TableHead
                                        className={`w-[60px] text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                      >
                                        Afbeelding
                                      </TableHead>
                                    )}
                                    <TableHead
                                      className={`w-[200px] text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                    >
                                      Naam
                                    </TableHead>
                                    {/* Other fields */}
                                    {(() => {
                                      const fields =
                                        categoryFields[category]?.filter(
                                          (field) =>
                                            field !== "name" &&
                                            field !== "isVisible" &&
                                            field !== "_fieldMetadata" &&
                                            field !== "_translations" &&
                                            // Don't include image fields in regular columns since we show them separately
                                            !getSortedItems(category).some(
                                              (item) => {
                                                const imgField =
                                                  findImageField(item);
                                                return (
                                                  imgField &&
                                                  imgField.field === field
                                                );
                                              },
                                            ),
                                        ) || [];

                                      // Find other fields (not number fields)
                                      const otherFields = fields.filter(
                                        (field) => !isNumberField(field),
                                      );

                                      // Show other fields (max 3 total)
                                      return otherFields
                                        .slice(0, 3)
                                        .map((field) => (
                                          <TableHead
                                            key={field}
                                            className={`text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                          >
                                            {getFieldDisplayName(field)}
                                            {/_unit$/.test(field) && (
                                              <span
                                                className={`ml-1 text-[10px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                              >
                                                ({getFieldUnit(field)})
                                              </span>
                                            )}
                                          </TableHead>
                                        ));
                                    })()}
                                    <TableHead
                                      className={`w-[100px] text-right text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                    >
                                      Acties
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {getSortedItems(category).map((item) => {
                                    // Find image field if exists
                                    const imageData = findImageField(item);
                                    console.log("Image data found:", imageData);

                                    return (
                                      <TableRow
                                        key={item.id}
                                        draggable
                                        onDragStart={onRowDragStart(
                                          category,
                                          item,
                                        )}
                                        onDragOver={onRowDragOver}
                                        onDrop={onRowDrop(category, item)}
                                        className={`transition-colors ${isDark ? "border-white/[0.04]" : "border-gray-50"} ${
                                          isItemBeingEdited(category, item.id)
                                            ? isDark
                                              ? "bg-white/[0.06]"
                                              : "bg-gray-50"
                                            : selectedItems.has(item.id)
                                              ? isDark
                                                ? "bg-white/[0.04]"
                                                : "bg-gray-50/80"
                                              : item.isVisible === false
                                                ? isDark
                                                  ? "opacity-40"
                                                  : "bg-gray-50/80 opacity-75 grayscale-[0.5]"
                                                : isDark
                                                  ? "hover:bg-white/[0.02]"
                                                  : "hover:bg-gray-50/50"
                                        }`}
                                      >
                                        {/* Checkbox */}
                                        <TableCell className="w-[48px] p-0 pl-2 align-middle">
                                          <Checkbox
                                            checked={selectedItems.has(item.id)}
                                            onCheckedChange={(checked) =>
                                              handleSelectItem(
                                                item.id,
                                                !!checked,
                                              )
                                            }
                                          />
                                        </TableCell>
                                        {/* Drag handle */}
                                        <TableCell className="w-[32px] p-0 align-middle">
                                          <div
                                            className={`h-full flex items-center justify-center cursor-move opacity-40 hover:opacity-100 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                          >
                                            <GripVertical className="h-3.5 w-3.5" />
                                          </div>
                                        </TableCell>
                                        {/* Image field first */}
                                        {getFilteredItems(category).some(
                                          (item) => findImageField(item),
                                        ) && (
                                          <TableCell className="w-[60px]">
                                            {(() => {
                                              const imageData =
                                                findImageField(item);
                                              if (!imageData) return null;

                                              let src = null;
                                              // Geval 1: Direct image URL (bijv. van een externe bron)
                                              if (
                                                imageData.value &&
                                                typeof imageData.value ===
                                                  "string" &&
                                                imageData.value.startsWith(
                                                  "images",
                                                )
                                              ) {
                                                src =
                                                  S3_BASE_URL +
                                                  "/" +
                                                  imageData.value;

                                                console.log(
                                                  "Using direct image URL:",
                                                  src,
                                                );
                                              }

                                              // Geval 3: Legacy nummeriek ID (Oude items)
                                              // Als je nog oude items hebt met een nummer als ID, werken die nu niet meer direct.
                                              // Voor 100% performance wil je die eigenlijk converteren, maar als fallback
                                              // zou je hier een enkele 'img' tag kunnen maken die naar je backend wijst:
                                              else if (
                                                imageData.isId &&
                                                typeof imageData.value ===
                                                  "number"
                                              ) {
                                                // Dit blijft via de server gaan, maar alleen voor oude items
                                                // Let op: je hebt hier de auth token nodig als je images protected zijn,
                                                // maar als je stap 1 uit mijn vorige antwoord hebt gevolgd (public bucket),
                                                // kun je hier niet veel mee tenzij je de bestandsnaam weet.
                                                // Voor nu is het veiliger om legacy items even als "ID: 123" te tonen
                                                // of te migreren.
                                                return (
                                                  <span className="text-xs text-gray-400">
                                                    ID: {imageData.value}
                                                  </span>
                                                );
                                              }

                                              if (src) {
                                                return (
                                                  <img
                                                    src={src}
                                                    alt="Item afbeelding"
                                                    className="h-12 w-12 object-cover rounded-md aspect-square"
                                                    loading="lazy" // Goed voor performance!
                                                  />
                                                );
                                              }

                                              return (
                                                <span className="text-gray-300">
                                                  -
                                                </span>
                                              );
                                            })()}
                                          </TableCell>
                                        )}
                                        {/* Number fields (like Volgnummer) - HIDDEN as requested */}
                                        <TableCell
                                          className={`text-[13px] font-medium max-w-[180px] ${isDark ? "text-white" : "text-gray-900"}`}
                                        >
                                          <span className="truncate block">
                                            {getDisplayValue(item.name)}
                                          </span>
                                        </TableCell>
                                        {/* Other fields */}
                                        {(() => {
                                          const fields =
                                            categoryFields[category]?.filter(
                                              (field) => {
                                                // Don't show name field or image fields (since we show them separately)
                                                if (
                                                  field === "name" ||
                                                  field === "isVisible" ||
                                                  field === "_fieldMetadata" ||
                                                  field === "_translations"
                                                )
                                                  return false;
                                                const imgField =
                                                  findImageField(item);
                                                return !(
                                                  imgField &&
                                                  imgField.field === field
                                                );
                                              },
                                            ) || [];

                                          // Find other fields (not number fields)
                                          const otherFields = fields.filter(
                                            (field) => !isNumberField(field),
                                          );

                                          // Show other fields (max 3 total)
                                          return otherFields
                                            .slice(0, 3)
                                            .map((field) => (
                                              <TableCell
                                                key={field}
                                                className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
                                              >
                                                <span className="truncate block max-w-[100px]">
                                                  {(() => {
                                                    const value = item[field];
                                                    const fieldType =
                                                      detectFieldType(
                                                        field,
                                                        value,
                                                      );

                                                    if (
                                                      value === null ||
                                                      value === undefined ||
                                                      value === ""
                                                    ) {
                                                      return (
                                                        <span
                                                          className={`italic ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                                        >
                                                          —
                                                        </span>
                                                      );
                                                    }

                                                    switch (fieldType) {
                                                      case "checkbox":
                                                        return value
                                                          ? "Ja"
                                                          : "Nee";
                                                      case "select":
                                                        // For category-linked selects ("<cat>_select")
                                                        // resolve the name LIVE from the source list so a
                                                        // rename of the source item shows up immediately.
                                                        // Manual selects fall through to their literal value.
                                                        return resolveSelectDisplay(
                                                          field,
                                                          value,
                                                          items,
                                                        );
                                                      case "array":
                                                        if (
                                                          Array.isArray(
                                                            value,
                                                          ) &&
                                                          value.length > 0
                                                        ) {
                                                          const firstItem =
                                                            typeof value[0] ===
                                                            "object"
                                                              ? getDisplayValue(
                                                                  value[0],
                                                                )
                                                              : value[0];
                                                          return value.length >
                                                            1
                                                            ? `${firstItem}... (+${
                                                                value.length - 1
                                                              })`
                                                            : firstItem;
                                                        }
                                                        return "Leeg";
                                                      case "image":
                                                        return "[Afbeelding]";
                                                      case "number":
                                                        return String(value);
                                                      case "unit": {
                                                        const unit =
                                                          getFieldUnit(field);
                                                        return `${value}${
                                                          unit ? ` ${unit}` : ""
                                                        }`;
                                                      }
                                                      case "date":
                                                        return String(value);
                                                      default:
                                                        // Handle potential translation objects
                                                        return String(
                                                          getDisplayValue(
                                                            value,
                                                          ),
                                                        );
                                                    }
                                                  })()}
                                                </span>
                                              </TableCell>
                                            ));
                                        })()}
                                        {/* Actions column */}
                                        <TableCell className="text-right">
                                          <div className="flex justify-end gap-1">
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <Button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleToggleVisibility(
                                                        category,
                                                        item.id,
                                                        item.isVisible,
                                                      );
                                                    }}
                                                    size="sm"
                                                    variant="ghost"
                                                    className={`h-7 w-7 p-0 rounded-lg ${
                                                      item.isVisible !== false
                                                        ? isDark
                                                          ? "text-gray-400 hover:bg-white/[0.06]"
                                                          : "text-gray-500 hover:bg-gray-100"
                                                        : isDark
                                                          ? "text-gray-600 hover:bg-white/[0.06]"
                                                          : "text-gray-300 hover:bg-gray-100"
                                                    }`}
                                                  >
                                                    {item.isVisible !==
                                                    false ? (
                                                      <Eye className="h-3.5 w-3.5" />
                                                    ) : (
                                                      <EyeOff className="h-3.5 w-3.5" />
                                                    )}
                                                  </Button>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                  className={`text-[11px] ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
                                                >
                                                  {item.isVisible !== false
                                                    ? "Zichtbaar"
                                                    : "Verborgen"}
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>

                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <Button
                                                    onClick={() =>
                                                      handleStartEditing(
                                                        category,
                                                        item,
                                                      )
                                                    }
                                                    size="sm"
                                                    variant="ghost"
                                                    className={`h-7 w-7 p-0 rounded-lg ${isDark ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
                                                  >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                  </Button>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                  className={`text-[11px] ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
                                                >
                                                  Bewerken
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>

                                            <AlertDialog>
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <AlertDialogTrigger asChild>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className={`h-7 w-7 p-0 rounded-lg ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                                                      >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                      </Button>
                                                    </AlertDialogTrigger>
                                                  </TooltipTrigger>
                                                  <TooltipContent
                                                    className={`text-[11px] ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
                                                  >
                                                    Verwijderen
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                              <AlertDialogContent
                                                className={`rounded-xl border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
                                              >
                                                <AlertDialogHeader>
                                                  <AlertDialogTitle className="text-red-500 text-[14px] font-semibold">
                                                    Item verwijderen
                                                  </AlertDialogTitle>
                                                  <AlertDialogDescription
                                                    className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                                  >
                                                    Weet u zeker dat u dit item
                                                    wilt verwijderen? Deze actie
                                                    kan niet ongedaan worden
                                                    gemaakt.
                                                  </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter className="gap-2">
                                                  <AlertDialogCancel
                                                    className={`h-9 rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                                                  >
                                                    Annuleren
                                                  </AlertDialogCancel>
                                                  <AlertDialogAction
                                                    onClick={() =>
                                                      handleDeleteItem(
                                                        category,
                                                        item.id,
                                                      )
                                                    }
                                                    className="bg-red-600 hover:bg-red-700 text-white h-9 rounded-lg text-[12px]"
                                                  >
                                                    Verwijderen
                                                  </AlertDialogAction>
                                                </AlertDialogFooter>
                                              </AlertDialogContent>
                                            </AlertDialog>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </>
                        ) : (
                          /* No items found message */
                          <div className="text-center py-10 px-4">
                            <div
                              className={`w-10 h-10 mx-auto mb-3 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                            >
                              <Search
                                className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              />
                            </div>
                            <p
                              className={`text-[13px] mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Geen items gevonden.
                            </p>
                            <p
                              className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            >
                              Probeer andere zoektermen
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </motion.div>
        )}

        {/* Footer actions */}
        {/* Delete all items button removed as requested */}
      </div>
    </div>
  );
};

export default ItemsManager;
